import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchAuditoriaOrcamentos,
  fetchAuditoriaTimelineOrcamento,
} from '../../../api/orcamentoCiliaApi';
import {
  AuditoriaOrcamentoItem,
  AuditoriaEventoCilia,
  AuditoriaTimelineCilia,
  rotuloDoAutor,
} from '../../../interfaces/OrcamentoCilia';
import { notify } from '../../../shared/feedback/notify';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import AuditoriaItensModal from './AuditoriaItensModal';
import AuditoriaDiffComplementoModal from './AuditoriaDiffComplementoModal';
import SearchIcon from '../../../img/search-icon.svg';
import FilterIcon from '../../../img/filter.svg';
import CleanIcon from '../../../img/cross-circle.svg';
import OrcamentoIcon from '../../../img/orcamento.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import '../../../components/como-funciona/help-circle-btn.css';
// As duas folhas consumidas por este componente, importadas explicitamente (BUG-V74):
// depender do CSS que a página hospedeira carrega é acoplamento invisível.
import '../../recepcao/pages/Recepcao.css';
import '../pages/AuditoriaPage.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Auditoria de ORÇAMENTOS (at-05, 16/08/2026) — a terceira aba da tela de Auditoria.
 *
 * A estrutura espelha as outras duas (mesma fileira de controles, mesma barra de contagem,
 * mesmo par "Resumo Atual" + "Linha do Tempo", mesmas classes `auditoria-*`): três metades
 * da mesma tela que divergissem no visual seriam lidas como telas diferentes.
 *
 * A humanização vem PRONTA do backend do módulo — aqui não há mapa de `action`, e quem
 * decide se um evento é clicável é o campo `abrir` que veio de lá.
 *
 * ⚠️ A unidade auditada é a **CADEIA** (número do Cilia), não a versão: um orçamento com 3
 * complementos são 4 registros no banco e uma história só para quem audita.
 */

const ORIGEM_LABEL: Record<AuditoriaEventoCilia['origem'], string> = {
  INTERNO: 'Equipe',
  SISTEMA: 'Sistema',
};

/** Rótulos de estado do orçamento — exportado porque a página reusa na lista do resumo do veículo. */
export const ESTADO_LABEL: Record<string, string> = {
  EM_CONFERENCIA: 'Em Conferência',
  ENVIADO: 'Conferência Concluída',
  AGUARDANDO_APROVACAO: 'Aguardando Aprovação',
  APROVADO: 'Requisição Aprovada',
  REJEITADO: 'Requisição Reprovada',
  EM_AJUSTE: 'Em Ajuste',
  AGUARDANDO_AUTORIZACAO: 'Aguardando Autorização',
  AUTORIZACAO_NEGADA: 'Autorização Negada',
};

const ORCAMENTOS_POR_PAGINA = 10;

interface FiltrosCilia {
  dataInicial: string;
  dataFinal: string;
  origem: string;
  autor: string;
  peca: string;
}

const FILTROS_VAZIOS: FiltrosCilia = { dataInicial: '', dataFinal: '', origem: '', autor: '', peca: '' };

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

function dataCurta(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

interface AuditoriaCiliaProps {
  /**
   * Orçamento a abrir já selecionado (16/08/2026): é como a auditoria do VEÍCULO chega
   * aqui — o card do veículo lista os orçamentos da placa e o clique troca de aba trazendo
   * esta prop. Consumida uma única vez, no mount (espelha o `cotacaoInicial` da aba de
   * Cotações).
   */
  orcamentoInicial?: AuditoriaOrcamentoItem | null;
  onOrcamentoInicialConsumido?: () => void;
  /** Ponte para a auditoria do VEÍCULO (a placa do resumo leva para a outra aba). */
  onAbrirVeiculo?: (placa: string) => void;
}

const AuditoriaCilia: React.FC<AuditoriaCiliaProps> = ({
  orcamentoInicial = null,
  onOrcamentoInicialConsumido,
  onAbrirVeiculo,
}) => {
  const [selecionado, setSelecionado] = useState<AuditoriaOrcamentoItem | null>(null);
  const [modalOrcamentosAberto, setModalOrcamentosAberto] = useState(false);
  const [buscaInput, setBuscaInput] = useState('');
  const [pagina, setPagina] = useState(1);
  const [opcoes, setOpcoes] = useState<AuditoriaOrcamentoItem[]>([]);
  const [totalOpcoes, setTotalOpcoes] = useState(0);
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState<string | null>(null);

  const [timeline, setTimeline] = useState<AuditoriaTimelineCilia | null>(null);
  const [carregandoTimeline, setCarregandoTimeline] = useState(false);
  const [erroTimeline, setErroTimeline] = useState<string | null>(null);

  const [filtros, setFiltros] = useState<FiltrosCilia>(FILTROS_VAZIOS);
  const [filtrosTemp, setFiltrosTemp] = useState<FiltrosCilia>(FILTROS_VAZIOS);
  const [modalFiltrosAberto, setModalFiltrosAberto] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [aberto, setAberto] = useState<AuditoriaEventoCilia | null>(null);

  /*
   * Erro NUNCA vira estado vazio (regra do at-60). Numa tela de auditoria isso é mais grave
   * que o normal: lista vazia por falha é indistinguível de "não há registro", e quem
   * investiga tira a conclusão oposta à correta.
   */
  const buscar = useCallback(async (termo: string, page: number) => {
    setBuscando(true);
    try {
      const resposta = await fetchAuditoriaOrcamentos(termo, page, ORCAMENTOS_POR_PAGINA);
      setOpcoes(resposta.data);
      setTotalOpcoes(resposta.total);
      setErroBusca(null);
    } catch (error) {
      setOpcoes([]);
      setTotalOpcoes(0);
      setErroBusca('Não foi possível buscar os orçamentos. Tente novamente; se persistir, acione a TI.');
      notify.errorFrom(error, 'Não foi possível buscar os orçamentos.');
    } finally {
      setBuscando(false);
    }
  }, []);

  useEffect(() => {
    if (!modalOrcamentosAberto) return;
    const id = setTimeout(() => buscar(buscaInput, pagina), 300);
    return () => clearTimeout(id);
  }, [modalOrcamentosAberto, buscaInput, pagina, buscar]);

  const carregarTimeline = useCallback(async (orcamentoId: string) => {
    setCarregandoTimeline(true);
    try {
      const dados = await fetchAuditoriaTimelineOrcamento(orcamentoId);
      setTimeline(dados);
      setErroTimeline(null);
    } catch (error) {
      setTimeline(null);
      setErroTimeline(
        'Não foi possível carregar a auditoria deste orçamento. Tente novamente; se persistir, acione a TI.',
      );
      notify.errorFrom(error, 'Não foi possível carregar a auditoria deste orçamento.');
    } finally {
      setCarregandoTimeline(false);
    }
  }, []);

  function escolher(orcamento: AuditoriaOrcamentoItem) {
    setSelecionado(orcamento);
    setModalOrcamentosAberto(false);
    setFiltros(FILTROS_VAZIOS);
    carregarTimeline(orcamento.id);
  }

  /*
   * Vindo da auditoria do VEÍCULO: abre já com o orçamento selecionado. Roda uma vez, no
   * mount — o componente é montado a cada entrada na aba, e o consumo avisa o pai para não
   * reaplicar o mesmo orçamento numa visita futura.
   */
  useEffect(() => {
    if (orcamentoInicial) {
      escolher(orcamentoInicial);
      onOrcamentoInicialConsumido?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function limpar() {
    setSelecionado(null);
    setTimeline(null);
    setErroTimeline(null);
    setFiltros(FILTROS_VAZIOS);
  }

  /*
   * Filtro no CLIENTE: a timeline de uma cadeia vem inteira numa requisição (dezenas de
   * eventos), como na aba de Cotações. O filtro por PEÇA é o que transforma a tela em
   * ferramenta de conferência — "o que aconteceu com este código?" — e casa contra o texto
   * já humanizado pelo backend, que é onde código e descrição aparecem.
   */
  const eventosFiltrados = useMemo(() => {
    const todos = timeline?.eventos ?? [];
    const peca = filtros.peca.trim().toLowerCase();
    return todos.filter((e) => {
      if (filtros.origem && e.origem !== filtros.origem) return false;
      if (filtros.autor && e.autor !== filtros.autor) return false;
      if (filtros.dataInicial && new Date(e.em) < new Date(`${filtros.dataInicial}T00:00:00`)) return false;
      if (filtros.dataFinal && new Date(e.em) > new Date(`${filtros.dataFinal}T23:59:59`)) return false;
      if (peca) {
        const alvo = [e.titulo, ...e.detalhes].join(' ').toLowerCase();
        if (!alvo.includes(peca)) return false;
      }
      return true;
    });
  }, [timeline, filtros]);

  const filtrosAtivos = Object.values(filtros).filter(Boolean).length;
  const totalPaginas = Math.max(1, Math.ceil(totalOpcoes / ORCAMENTOS_POR_PAGINA));
  const orcamento = timeline?.orcamento;

  const ajudaSecoes: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta aba mostra',
      conteudo: (
        <p>
          Todo o histórico de um orçamento do Cilia: a importação do XML, o que a conferência decidiu peça
          por peça, o que foi criado no Nasajon, a requisição de compra criada lá e o desfecho
          dela. A unidade é o <strong>orçamento inteiro</strong> — se ele tem complementos, todas as versões
          aparecem na mesma linha do tempo, porque é assim que a história faz sentido.
        </p>
      ),
    },
    {
      titulo: 'Também aparece o que o sistema decidiu sozinho',
      conteudo: (
        <p>
          Nem todo evento tem uma pessoa por trás. <strong>Esteira preenchida automaticamente</strong> pelo
          aprendizado de importações anteriores, avisos automáticos de peça sem verificação no ERP, peça
          reclassificada na importação de um complemento: tudo isso é decisão do sistema e aparece com o autor{' '}
          <strong>Sistema</strong>. São justamente as mudanças que ninguém pediu e que, sem registro, seriam
          impossíveis de explicar depois.
        </p>
      ),
    },
    {
      titulo: 'Eventos que abrem detalhes',
      conteudo: (
        <div>
          <p>
            Os títulos <strong>clicáveis</strong> abrem a lista de peças daquele momento: a importação mostra
            o que entrou e como cada peça foi classificada; o <strong>complemento</strong> mostra o que saiu e
            o que veio de novo em relação à versão anterior; a <strong>conclusão</strong> mostra as peças que
            entraram na requisição e quais foram criadas no ERP.
          </p>
          <p>
            As listas são a <strong>fotografia daquele instante</strong> — não refletem alterações posteriores,
            que têm o seu próprio registro na linha do tempo.
          </p>
        </div>
      ),
    },
    {
      titulo: 'Tentativas de importação recusadas',
      conteudo: (
        <p>
          XML recusado também vira registro: complemento repetido, versão anterior ainda em aberto, placa que
          não confere com o atendimento. Serve para responder "por que este orçamento demorou a entrar" sem
          depender da memória de quem tentou.
        </p>
      ),
    },
    {
      titulo: 'Registros antigos',
      conteudo: (
        <p>
          Registros anteriores a agosto de 2026 podem aparecer com um código no lugar do nome de quem agiu, e
          alguns abrem sem o detalhamento das peças: essas informações passaram a ser guardadas a partir dessa
          data e não há como recuperá-las para trás. O evento continua na linha do tempo — o que falta é o
          detalhe, nunca o registro.
        </p>
      ),
    },
  ];

  return (
    <>
      <div className="VisualizarOrdenar">
        <div
          className="Frame1000004037"
          style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}
        >
          <div
            className="CtaBig MouseHover"
            onClick={() => { setBuscaInput(''); setPagina(1); setModalOrcamentosAberto(true); }}
          >
            <div className="Frame1000003987">
              <img src={OrcamentoIcon} alt="" width="20" height="20" />
              <div className="ComponentName">Orçamentos</div>
            </div>
          </div>

          <div className="CtaBig MouseHover" onClick={() => { setFiltrosTemp(filtros); setModalFiltrosAberto(true); }}>
            <div className="Frame1000003987">
              <img src={FilterIcon} alt="" width="19.8" height="19.8" />
              <div className="ComponentName">Filtros {filtrosAtivos > 0 ? `(${filtrosAtivos})` : ''}</div>
            </div>
          </div>

          <div className="CtaBig MouseHover" onClick={limpar}>
            <div className="Frame1000003987">
              <img src={CleanIcon} alt="" width="20" height="20" />
              <div className="ComponentName">Limpar</div>
            </div>
          </div>
        </div>
      </div>

      <div className="QuantidadeDeCarros auditoria-summary-bar">
        <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}>
          <div className="CtaQuantidadeDeCarros">
            <div className="Frame1000003987">
              <div className="ComponentName">
                {selecionado
                  ? eventosFiltrados.length < (timeline?.eventos.length ?? 0)
                    ? `${eventosFiltrados.length} de ${timeline?.eventos.length} eventos exibidos para o orçamento ${selecionado.numeroCilia}`
                    : `${eventosFiltrados.length} eventos encontrados para o orçamento ${selecionado.numeroCilia}`
                  : 'Nenhum orçamento selecionado'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="auditoria-layout">
        <div className="auditoria-summary-card">
          <div className="auditoria-card-heading">Resumo Atual</div>

          {!selecionado && (
            <div className="auditoria-empty-state">
              Nenhum orçamento selecionado. Por favor, selecione um orçamento para visualizar a auditoria.
            </div>
          )}

          {selecionado && orcamento && (
            <>
              <div className="auditoria-hero-row">
                <div>
                  <div className="auditoria-hero-title">Orçamento {orcamento.numeroCilia}</div>
                  <div className="auditoria-hero-subtitle">{orcamento.veiculo || '—'}</div>
                </div>
                <span className="auditoria-status-badge" style={{ background: 'var(--tema-fundo, #fff4ee)', color: 'var(--tema-texto-laranja, #b4541a)' }}>
                  {ESTADO_LABEL[orcamento.estadoAtual ?? ''] ?? orcamento.estadoAtual ?? '—'}
                </span>
              </div>
              <div className="auditoria-summary-grid">
                {/* Ponte para a auditoria do VEÍCULO — espelho da placa no resumo de cotações. */}
                <div>
                  <strong>Placa:</strong>{' '}
                  {orcamento.placa && onAbrirVeiculo ? (
                    <button
                      type="button"
                      className="auditoria-link-veiculo"
                      title="Abrir a auditoria deste veículo"
                      onClick={() => onAbrirVeiculo(orcamento.placa as string)}
                    >
                      {orcamento.placa}
                    </button>
                  ) : (
                    orcamento.placa || '—'
                  )}
                </div>
                <div><strong>Evento:</strong> {orcamento.numeroEvento || '—'}</div>
                <div><strong>Importado em:</strong> {dataCurta(orcamento.createdAt)}</div>
                <div><strong>Importado por:</strong> {orcamento.importadoPorNome || '—'}</div>
                <div><strong>Versões:</strong> {orcamento.qtdVersoes}</div>
              </div>
            </>
          )}
        </div>

        <div className="auditoria-timeline-card">
          <div className="auditoria-card-heading" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Linha do Tempo</span>
            <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setAjudaAberta(true)}>
              ?
            </button>
          </div>

          {erroTimeline && (
            <div className="auditoria-error-state" role="alert">
              {erroTimeline}
            </div>
          )}

          {!erroTimeline && !selecionado && (
            <div className="auditoria-empty-state">
              Por favor, selecione um orçamento para visualizar a linha do tempo de auditoria.
            </div>
          )}

          {carregandoTimeline && <div className="auditoria-empty-state">Carregando a linha do tempo...</div>}

          {!erroTimeline && !carregandoTimeline && selecionado && eventosFiltrados.length === 0 && (
            <div className="auditoria-empty-state">Nenhum evento encontrado para os filtros informados.</div>
          )}

          {!carregandoTimeline && eventosFiltrados.length > 0 && (
            <div className="auditoria-timeline-list">
              {eventosFiltrados.map((evento) => (
                <div key={evento.id} className="auditoria-timeline-item">
                  <div className="auditoria-timeline-rail">
                    <div className={`auditoria-timeline-icon auditoria-timeline-icon--${evento.origem.toLowerCase()}`}>
                      {evento.origem === 'SISTEMA' ? 'S' : 'E'}
                    </div>
                    <div className="auditoria-timeline-line" />
                  </div>

                  <div className="auditoria-event-card">
                    <div className="auditoria-event-topline">
                      <span className="auditoria-event-date">{dataHora(evento.em)}</span>
                      <span className="auditoria-event-type">{ORIGEM_LABEL[evento.origem]}</span>
                    </div>

                    <div className="auditoria-event-title-row">
                      {evento.abrir ? (
                        <button type="button" className="auditoria-event-title-link-btn" onClick={() => setAberto(evento)}>
                          {evento.titulo}
                        </button>
                      ) : (
                        <strong>{evento.titulo}</strong>
                      )}
                    </div>

                    <div className="auditoria-event-description">
                      {evento.detalhes.length > 0 ? evento.detalhes.join(' · ') : 'Sem descrição complementar.'}
                    </div>

                    <div className="auditoria-event-meta-grid">
                      {evento.abrir && (
                        <div>
                          <strong>Detalhe:</strong>{' '}
                          {evento.abrir.tipo === 'DIFF_COMPLEMENTO'
                            ? 'Clique no título para ver o que mudou em relação à versão anterior.'
                            : evento.abrir.tipo === 'REVERIFICACAO'
                              ? 'Clique no título para ver quais peças foram verificadas.'
                              : evento.abrir.tipo === 'ITENS_DEVOLVIDOS'
                                ? 'Clique no título para ver quais peças voltaram para a compra.'
                                : 'Clique no título para ver as peças deste registro.'}
                        </div>
                      )}
                      {/*
                        Nunca imprimir UUID (16/08/2026): quando o nome não foi resolvido,
                        a tela diz "usuário não identificado" e o id fica no `title`.
                      */}
                      <div>
                        <strong>Registrado por:</strong>{' '}
                        <span title={rotuloDoAutor(evento).title}>{rotuloDoAutor(evento).texto}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Modal: selecionar orçamento ───────────────────────────────────── */}
      {modalOrcamentosAberto && (
        <div className="auditoria-modal-overlay" role="presentation" onClick={() => setModalOrcamentosAberto(false)}>
          <div className="auditoria-modal" role="dialog" aria-modal="true" aria-label="Selecionar orçamento" onClick={(e) => e.stopPropagation()}>
            <div className="auditoria-modal-header">
              <h3>Selecionar orçamento</h3>
              <button className="close-button" onClick={() => setModalOrcamentosAberto(false)}>&times;</button>
            </div>

            <div className="auditoria-modal-body">
              <div className="auditoria-modal-search-wrapper">
                <img className="search-icon-inside" src={SearchIcon} alt="" width="18" height="18" />
                <input
                  className="auditoria-modal-search"
                  type="text"
                  placeholder="Buscar por número do orçamento, placa ou evento..."
                  value={buscaInput}
                  onChange={(e) => { setBuscaInput(e.target.value); setPagina(1); }}
                  aria-label="Buscar orçamento"
                  autoFocus
                />
              </div>

              {erroBusca ? (
                <div className="auditoria-error-state" role="alert">{erroBusca}</div>
              ) : (
                <div className="auditoria-modal-count">{totalOpcoes} orçamento(s) encontrado(s)</div>
              )}

              {buscando ? (
                <div className="auditoria-modal-loading">
                  <div className="spinner"></div>
                  <p>Buscando orçamentos...</p>
                </div>
              ) : (
                <div className="auditoria-modal-list" role="listbox" aria-label="Lista de orçamentos">
                  {!erroBusca && opcoes.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--tema-texto-3, #6b7280)', fontFamily: 'AmpleSoft-Regular, sans-serif' }}>
                      Nenhum orçamento encontrado para o filtro informado.
                    </div>
                  )}
                  {opcoes.map((o) => (
                    <div
                      key={o.id}
                      className={`auditoria-modal-option ${selecionado?.id === o.id ? 'is-selected' : ''}`}
                      role="option"
                      aria-selected={selecionado?.id === o.id}
                      onClick={() => escolher(o)}
                    >
                      <div className="auditoria-modal-avatar is-cotacao">
                        <img src={OrcamentoIcon} alt="" style={{ width: '20px', height: '20px' }} />
                      </div>
                      <div className="auditoria-modal-info">
                        <span className="auditoria-modal-name">
                          {o.numeroCilia}
                          {o.qtdVersoes > 1 ? ` (${o.qtdVersoes} versões)` : ''}
                        </span>
                        <span
                          className="auditoria-modal-username"
                          title={[o.placa, o.veiculo].filter(Boolean).join(' · ')}
                        >
                          {[o.placa, o.veiculo].filter(Boolean).join(' · ') || '—'}
                        </span>
                      </div>
                      <span className="auditoria-modal-tag">
                        {ESTADO_LABEL[o.estadoAtual ?? ''] ?? o.estadoAtual ?? '—'}
                      </span>
                      {selecionado?.id === o.id && (
                        <div className="auditoria-modal-selected-indicator">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="auditoria-modal-pagination">
                <div
                  className={`auditoria-modal-page-btn ${pagina === 1 ? 'disabled' : ''}`}
                  onClick={() => pagina > 1 && setPagina(pagina - 1)}
                  style={{ cursor: pagina === 1 ? 'not-allowed' : 'pointer' }}
                >
                  <img className="imgIcon" src={pagina === 1 ? ArrowLeftGray : ArrowLeftOrange} alt="Seta para Esquerda" width="24" height="24" />
                  <div className="Anterior" style={{ color: pagina === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)', fontSize: '14px', fontWeight: 600 }}>
                    Anterior
                  </div>
                </div>
                <span style={{ fontSize: '13px', color: 'var(--tema-texto-2, #4b5563)', fontFamily: 'AmpleSoft-Bold, sans-serif', fontWeight: 700 }}>
                  Página {pagina} de {totalPaginas}
                </span>
                <div
                  className={`auditoria-modal-page-btn ${pagina >= totalPaginas ? 'disabled' : ''}`}
                  onClick={() => pagina < totalPaginas && setPagina(pagina + 1)}
                  style={{ cursor: pagina >= totalPaginas ? 'not-allowed' : 'pointer' }}
                >
                  <div className="PrXima" style={{ color: pagina >= totalPaginas ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)', fontSize: '14px', fontWeight: 600 }}>
                    Próxima
                  </div>
                  <img className="imgIcon" src={pagina >= totalPaginas ? ArrowRightGray : ArrowRightOrange} alt="Seta para Direita" width="24" height="24" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: filtros ────────────────────────────────────────────────── */}
      {modalFiltrosAberto && (
        <div className="auditoria-modal-overlay" role="presentation" onClick={() => setModalFiltrosAberto(false)}>
          <div className="auditoria-modal auditoria-filters-modal" role="dialog" aria-modal="true" aria-label="Filtros de Auditoria" onClick={(e) => e.stopPropagation()}>
            <div className="auditoria-modal-header">
              <h3>Filtrar Eventos</h3>
              <button type="button" className="close-button" onClick={() => setModalFiltrosAberto(false)}>
                &times;
              </button>
            </div>

            <div className="auditoria-filters-modal-body">
              <div className="auditoria-filter-field">
                <label htmlFor="cilia-dataInicial">Data Inicial</label>
                <input
                  id="cilia-dataInicial"
                  type="date"
                  value={filtrosTemp.dataInicial}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, dataInicial: e.target.value }))}
                />
              </div>

              <div className="auditoria-filter-field">
                <label htmlFor="cilia-dataFinal">Data Final</label>
                <input
                  id="cilia-dataFinal"
                  type="date"
                  value={filtrosTemp.dataFinal}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, dataFinal: e.target.value }))}
                />
              </div>

              <div className="auditoria-filter-field">
                <label htmlFor="cilia-origem">Origem do Evento</label>
                <select
                  id="cilia-origem"
                  value={filtrosTemp.origem}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, origem: e.target.value }))}
                >
                  <option value="">Todas as origens</option>
                  <option value="INTERNO">Equipe</option>
                  <option value="SISTEMA">Sistema</option>
                </select>
              </div>

              <div className="auditoria-filter-field">
                <label htmlFor="cilia-autor">Autor</label>
                <select
                  id="cilia-autor"
                  value={filtrosTemp.autor}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, autor: e.target.value }))}
                  disabled={(timeline?.autores.length ?? 0) === 0}
                >
                  <option value="">
                    {(timeline?.autores.length ?? 0) === 0 ? 'Selecione um orçamento primeiro' : 'Todos os autores'}
                  </option>
                  {(timeline?.autores ?? []).map((autor) => (
                    <option key={autor} value={autor}>{autor}</option>
                  ))}
                </select>
              </div>

              {/*
                Filtro por PEÇA: é o que transforma a auditoria em ferramenta de conferência
                ("o que aconteceu com este código?") em vez de só prestação de contas.
              */}
              <div className="auditoria-filter-field">
                <label htmlFor="cilia-peca">Peça (código ou descrição)</label>
                <input
                  id="cilia-peca"
                  type="text"
                  placeholder="Ex.: 52068752 ou capô"
                  value={filtrosTemp.peca}
                  onChange={(e) => setFiltrosTemp((f) => ({ ...f, peca: e.target.value }))}
                />
              </div>
            </div>

            <div className="auditoria-filters-modal-actions">
              <button type="button" className="auditoria-btn-clear" onClick={() => setFiltrosTemp(FILTROS_VAZIOS)}>
                Limpar
              </button>
              <button
                type="button"
                className="auditoria-btn-apply"
                onClick={() => { setFiltros(filtrosTemp); setModalFiltrosAberto(false); }}
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {aberto?.abrir?.tipo === 'DIFF_COMPLEMENTO' && (
        <AuditoriaDiffComplementoModal
          diff={aberto.abrir.diff}
          itens={aberto.abrir.itens}
          em={aberto.em}
          autor={aberto.autor}
          titulo={aberto.titulo}
          onFechar={() => setAberto(null)}
        />
      )}

      {aberto?.abrir?.tipo === 'ITENS_IMPORTADOS' && (
        <AuditoriaItensModal
          itens={aberto.abrir.itens}
          titulo="Peças importadas"
          rotuloAutor="Importado por"
          em={aberto.em}
          autor={aberto.autor}
          onFechar={() => setAberto(null)}
        />
      )}

      {aberto?.abrir?.tipo === 'ITENS_ENVIADOS' && (
        <AuditoriaItensModal
          itens={aberto.abrir.itens}
          titulo={aberto.abrir.titulo}
          rotuloAutor="Concluído por"
          em={aberto.em}
          autor={aberto.autor}
          onFechar={() => setAberto(null)}
        />
      )}

      {aberto?.abrir?.tipo === 'REVERIFICACAO' && (
        <AuditoriaItensModal
          itens={aberto.abrir.itens}
          titulo="Peças reverificadas no Nasajon"
          em={aberto.em}
          autor={aberto.autor}
          onFechar={() => setAberto(null)}
        />
      )}

      {aberto?.abrir?.tipo === 'ITENS_DEVOLVIDOS' && (
        <AuditoriaItensModal
          itens={aberto.abrir.itens}
          titulo="Peças devolvidas para a compra"
          em={aberto.em}
          autor={aberto.autor}
          onFechar={() => setAberto(null)}
        />
      )}

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - Auditoria de Orçamentos"
        secoes={ajudaSecoes}
      />
    </>
  );
};

export default AuditoriaCilia;
